import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User } from '../types'
import { authApi } from '../api/auth'

export const useUserStore = defineStore('user', () => {
  // State
  const user = ref<User | null>(null)
  const token = ref<string>(localStorage.getItem('token') || '')
  const loading = ref(false)

  // Getters
  const isLoggedIn = computed(() => !!token.value && !!user.value)
  const isAdmin = computed(() => user.value?.is_admin || false)

  // Actions
  const setToken = (newToken: string) => {
    token.value = newToken
    localStorage.setItem('token', newToken)
  }

  const clearToken = () => {
    token.value = ''
    user.value = null
    localStorage.removeItem('token')
    localStorage.removeItem('api_token')
  }

  const setUser = (userData: User) => {
    user.value = userData
    // Also save api_token to localStorage for proxy extraction
    if (userData.api_token) {
      localStorage.setItem('api_token', userData.api_token)
    }
  }

  const login = async (username: string, password: string) => {
    loading.value = true
    try {
      const res = await authApi.login({ username, password })
      setToken(res.data.access_token)
      // Fetch user info including api_token
      const userRes = await authApi.getCurrentUser()
      setUser(userRes.data)
      return true
    } catch (error) {
      return false
    } finally {
      loading.value = false
    }
  }

  const register = async (data: {
    username: string
    email: string
    password: string
    confirmPassword: string
    phone?: string
  }) => {
    loading.value = true
    try {
      await authApi.register({
        username: data.username,
        email: data.email,
        password: data.password,
        confirmPassword: data.confirmPassword,
        phone: data.phone,
      })
      return true
    } catch (error) {
      return false
    } finally {
      loading.value = false
    }
  }

  const fetchUserInfo = async () => {
    try {
      const res = await authApi.getCurrentUser()
      setUser(res.data)
      return true
    } catch (error) {
      clearToken()
      return false
    }
  }

  const logout = async () => {
    try {
      await authApi.logout()
    } catch (error) {
      // Ignore error
    }
    clearToken()
  }

  const checkLogin = async () => {
    if (token.value && !user.value) {
      await fetchUserInfo()
    }
  }

  const refreshApiToken = async () => {
    try {
      const res = await authApi.regenerateApiToken()
      if (user.value) {
        user.value.api_token = res.data.api_token
      }
      // Save to localStorage for proxy extraction
      localStorage.setItem('api_token', res.data.api_token)
      return res.data.api_token
    } catch (error) {
      return null
    }
  }

  return {
    user,
    token,
    loading,
    isLoggedIn,
    isAdmin,
    setToken,
    clearToken,
    setUser,
    login,
    register,
    fetchUserInfo,
    logout,
    checkLogin,
    refreshApiToken,
  }
})
